import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, Loader2, AlertCircle, Cloud } from "lucide-react";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface SaveIndicatorProps {
  status: SaveStatus;
  className?: string;
  /** Custom message to display for each status */
  messages?: {
    idle?: string;
    saving?: string;
    saved?: string;
    error?: string;
  };
  /** Auto-dismiss saved state after this many milliseconds (default: 3000) */
  autoDismissDelay?: number;
  /** Callback when saved status should be dismissed */
  onDismiss?: () => void;
  /** Show the indicator even when idle */
  showWhenIdle?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

const defaultMessages = {
  idle: "All changes saved",
  saving: "Saving...",
  saved: "Saved",
  error: "Failed to save",
};

export function SaveIndicator({
  status,
  className,
  messages = {},
  autoDismissDelay = 3000,
  onDismiss,
  showWhenIdle = false,
  errorMessage,
}: SaveIndicatorProps) {
  const mergedMessages = { ...defaultMessages, ...messages };

  // Auto-dismiss saved state
  React.useEffect(() => {
    if (status === "saved" && autoDismissDelay > 0 && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissDelay);
      return () => clearTimeout(timer);
    }
  }, [status, autoDismissDelay, onDismiss]);

  // Don't render when idle unless explicitly requested
  if (status === "idle" && !showWhenIdle) {
    return null;
  }

  const getStatusConfig = () => {
    switch (status) {
      case "saving":
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
          message: mergedMessages.saving,
          colorClass: "text-blue-600",
          bgClass: "bg-blue-50",
        };
      case "saved":
        return {
          icon: <Check className="h-4 w-4" />,
          message: mergedMessages.saved,
          colorClass: "text-green-600",
          bgClass: "bg-green-50",
        };
      case "error":
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          message: errorMessage || mergedMessages.error,
          colorClass: "text-red-600",
          bgClass: "bg-red-50",
        };
      case "idle":
      default:
        return {
          icon: <Cloud className="h-4 w-4" />,
          message: mergedMessages.idle,
          colorClass: "text-gray-500",
          bgClass: "bg-gray-50",
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200",
        config.colorClass,
        config.bgClass,
        className
      )}
    >
      {config.icon}
      <span>{config.message}</span>
    </div>
  );
}

/**
 * Hook to manage save status with automatic transitions
 */
export function useSaveStatus(initialStatus: SaveStatus = "idle") {
  const [status, setStatus] = React.useState<SaveStatus>(initialStatus);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

  const setSaving = React.useCallback(() => {
    setStatus("saving");
    setErrorMessage(undefined);
  }, []);

  const setSaved = React.useCallback(() => {
    setStatus("saved");
    setErrorMessage(undefined);
  }, []);

  const setError = React.useCallback((message?: string) => {
    setStatus("error");
    setErrorMessage(message);
  }, []);

  const setIdle = React.useCallback(() => {
    setStatus("idle");
    setErrorMessage(undefined);
  }, []);

  const reset = React.useCallback(() => {
    setStatus("idle");
    setErrorMessage(undefined);
  }, []);

  return {
    status,
    errorMessage,
    setSaving,
    setSaved,
    setError,
    setIdle,
    reset,
    setStatus,
  };
}

/**
 * Inline save indicator for form fields
 */
interface InlineSaveIndicatorProps {
  status: SaveStatus;
  className?: string;
}

export function InlineSaveIndicator({
  status,
  className,
}: InlineSaveIndicatorProps) {
  if (status === "idle") return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        status === "saving" && "text-blue-600",
        status === "saved" && "text-green-600",
        status === "error" && "text-red-600",
        className
      )}
    >
      {status === "saving" && (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Saving...</span>
        </>
      )}
      {status === "saved" && (
        <>
          <Check className="h-3 w-3" />
          <span>Saved</span>
        </>
      )}
      {status === "error" && (
        <>
          <AlertCircle className="h-3 w-3" />
          <span>Error</span>
        </>
      )}
    </span>
  );
}
